import { useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import type { ResolvedLineMeasurement } from '../../model/screencast-tooltips/screencastLineMeasurementResolver';

import styles from './ScreencastTooltip.module.scss';

const TOOLTIP_OFFSET = 12;
const VIEWPORT_MARGIN = 8;
const VALUE_FRACTION_DIGITS = 2;
const ANGLE_FRACTION_DIGITS = 1;

type TooltipScreenPosition = {
  x: number;
  y: number;
};

type LineMeasurementTooltipRow = {
  label?: string;
  value: string;
};

type ScreencastLineMeasurementTooltipProps = {
  measurement: ResolvedLineMeasurement | null;
  cursorPosition: TooltipScreenPosition | null;
};

// eslint-disable-next-line mobx/missing-observer
export const ScreencastLineMeasurementTooltip = ({
  measurement,
  cursorPosition,
}: ScreencastLineMeasurementTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const rows = useMemo(() => {
    if (!measurement) {
      return [];
    }

    return getLineMeasurementTooltipRows(measurement);
  }, [measurement]);

  useLayoutEffect(() => {
    if (!measurement || !cursorPosition || rows.length === 0 || typeof window === 'undefined') {
      return;
    }

    const tooltipElement = tooltipRef.current;
    if (!tooltipElement) {
      return;
    }

    const tooltipRect = tooltipElement.getBoundingClientRect();
    const viewportMaxX = window.innerWidth - VIEWPORT_MARGIN;
    const viewportMaxY = window.innerHeight - VIEWPORT_MARGIN;

    let x = cursorPosition.x + TOOLTIP_OFFSET;
    let y = cursorPosition.y + TOOLTIP_OFFSET;

    if (x + tooltipRect.width > viewportMaxX) {
      x = cursorPosition.x - tooltipRect.width - TOOLTIP_OFFSET;
    }

    if (y + tooltipRect.height > viewportMaxY) {
      y = cursorPosition.y - tooltipRect.height - TOOLTIP_OFFSET;
    }

    tooltipElement.style.left = `${Math.max(VIEWPORT_MARGIN, x)}px`;
    tooltipElement.style.top = `${Math.max(VIEWPORT_MARGIN, y)}px`;
  }, [cursorPosition, measurement, rows.length]);

  if (!measurement || !cursorPosition || rows.length === 0) {
    return null;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className={`${styles.ScreencastTooltip} ${styles.ScreencastLineMeasurementTooltip}`}
      style={{
        left: cursorPosition.x + TOOLTIP_OFFSET,
        top: cursorPosition.y + TOOLTIP_OFFSET,
      }}
    >
      {rows.map((row, index) => (
        <div
          key={`${row.label ?? 'value'}-${index}`}
          className={row.label ? styles.ScreencastTooltipValueRow : styles.ScreencastTooltipSingleValueRow}
        >
          {row.label && <span className={styles.ScreencastTooltipLabel}>{row.label}</span>}
          <span className={styles.ScreencastTooltipValue}>{row.value}</span>
        </div>
      ))}
    </div>,
    document.body,
  );
};

function getLineMeasurementTooltipRows(measurement: ResolvedLineMeasurement): LineMeasurementTooltipRow[] {
  const rows: LineMeasurementTooltipRow[] = [
    {
      value: formatMeasurementValue(measurement.distance, measurement.units.distance),
    },
    {
      value: `${formatNumber(measurement.angle, {
        forceSign: true,
        maximumFractionDigits: ANGLE_FRACTION_DIGITS,
      })}\u00B0`,
    },
    {
      label: '\u2206 HPL',
      value: formatMeasurementValue(measurement.deltaHpl, measurement.units.hpl, { forceSign: true }),
    },
    {
      label: '\u2206 TVD',
      value: formatMeasurementValue(measurement.deltaVertical, measurement.units.vertical, { forceSign: true }),
    },
  ];

  if (measurement.tvdssAtEnd !== null) {
    rows.push({
      label: 'TVDSS',
      value: formatMeasurementValue(measurement.tvdssAtEnd, measurement.units.tvdss, { maximumFractionDigits: 1 }),
    });
  }

  return rows;
}

function formatMeasurementValue(
  value: number,
  unit: string | undefined,
  options: {
    forceSign?: boolean;
    maximumFractionDigits?: number;
  } = {},
) {
  const valueText = formatNumber(value, options);

  return unit ? `${valueText} ${unit}` : valueText;
}

function formatNumber(
  value: number,
  options: {
    forceSign?: boolean;
    maximumFractionDigits?: number;
  } = {},
) {
  const normalizedValue = Object.is(value, -0) ? 0 : value;
  const maximumFractionDigits = options.maximumFractionDigits ?? VALUE_FRACTION_DIGITS;
  const roundedAbsValue = roundToFractionDigits(Math.abs(normalizedValue), maximumFractionDigits);
  const formattedNumber = roundedAbsValue.toLocaleString(undefined, {
    maximumFractionDigits,
  });
  const sign = roundedAbsValue === 0 ? '' : normalizedValue < 0 ? '-' : options.forceSign ? '+' : '';
  return `${sign}${formattedNumber}`;
}

function roundToFractionDigits(value: number, fractionDigits: number): number {
  const multiplier = 10 ** fractionDigits;
  return Math.round(value * multiplier) / multiplier;
}
